import React, { useEffect, useState, useCallback } from 'react';
import { Card, Collapse, Table, Space, Card as AntCard, Row, Col, Button, Typography, Tag, Modal, InputNumber, message, Badge, Progress, Drawer, Divider, Input, Select } from 'antd';
import { ShopOutlined, EditOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, DeleteOutlined, EyeOutlined, ShoppingCartOutlined, DollarOutlined, SearchOutlined, ClearOutlined } from '@ant-design/icons';
import { useProdusToOfertaStatus } from '../../Oferte/hooks/useProdusToOfertaStatus'
import { useProducts } from "../hook/useProucts"
import { Link } from 'react-router-dom';

const { Text, Title } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

// Componentă pentru filtrele de căutare (mutată în afara pentru a evita recrearea)
const FiltersComponent = ({ 
  localSearchValue, 
  setLocalSearchValue, 
  localClientName, 
  setLocalClientName, 
  localManagerId, 
  setLocalManagerId,
  filters,
  applyFilters,
  statusOptions,
  handleKeyPress,
  handleApplyFilters,
  handleClearFilters,
  loading,
  isMobile 
}) => (
  <Card size="small" style={{ marginBottom: 16 }}>
    <Row gutter={[16, 16]} align="middle">
      <Col xs={24} sm={12} md={6} lg={5}>
        <Input
          placeholder="Căutare: produs, oferta_id, amo_id..."
          prefix={<SearchOutlined />}
          value={localSearchValue}
          onChange={(e) => setLocalSearchValue(e.target.value)}
          onKeyPress={handleKeyPress}
          allowClear
          onClear={() => setLocalSearchValue('')}
        />
      </Col>
      <Col xs={24} sm={12} md={6} lg={4}>
        <Input
          placeholder="Nume client..."
          value={localClientName}
          onChange={(e) => setLocalClientName(e.target.value)}
          onKeyPress={handleKeyPress}
          allowClear
          onClear={() => setLocalClientName('')}
        />
      </Col>
      <Col xs={24} sm={12} md={6} lg={5}>
        <Select
          placeholder="Status produse..."
          mode="multiple"
          value={filters.product_statuses}
          onChange={(value) => applyFilters({ ...filters, product_statuses: value })}
          style={{ width: '100%' }}
          allowClear
        >
          {statusOptions.map(status => (
            <Option key={status.id} value={status.id}>
              <Tag color={status.color} size="small">{status.label}</Tag>
            </Option>
          ))}
        </Select>
      </Col>
      <Col xs={24} sm={12} md={4} lg={3}>
        <Input
          placeholder="Manager ID..."
          type="number"
          value={localManagerId}
          onChange={(e) => setLocalManagerId(e.target.value)}
          onKeyPress={handleKeyPress}
          allowClear
          onClear={() => setLocalManagerId('')}
        />
      </Col>
      <Col xs={24} sm={12} md={4} lg={3}>
        <Button 
          type="primary"
          icon={<SearchOutlined />} 
          onClick={handleApplyFilters}
          block={isMobile}
          loading={loading}
        >
          Filtrare
        </Button>
      </Col>
      <Col xs={24} sm={12} md={4} lg={4}>
        <Button 
          icon={<ClearOutlined />} 
          onClick={handleClearFilters}
          block={isMobile}
        >
          Reset
        </Button>
      </Col>
    </Row>
  </Card>
);

const DecideFurnizori = () => {
  // State local
  const [isMobile, setIsMobile] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [newBoughtPrice, setNewBoughtPrice] = useState(0);
  
  // State local pentru search (nu trimite automat la server)
  const [localSearchValue, setLocalSearchValue] = useState('');
  const [localClientName, setLocalClientName] = useState('');
  const [localManagerId, setLocalManagerId] = useState('');
  
  // Hooks
  const { productStatusOptions: hookProductStatusOptions } = useProdusToOfertaStatus();
  const {  
    productsInPurchaseStatuses, 
    changeBoughtPriceForProductOffer,
    addProductToPurchaseRequest,
    deleteProductFromPurchaseRequest,
    organizedOferte,
    productStatusOptions,
    overallSummary,
    loading,
    pagination,
    filters,
    applyFilters,
    clearFilters,
    handleTableChange,
    setOrganizedOferte,
    fetchOrganizedData
  } = useProducts();

  // Folosim productStatusOptions din noul endpoint, cu fallback la hook-ul vechi
  const statusOptions = productStatusOptions?.length > 0 ? productStatusOptions : hookProductStatusOptions;

  // Detectare device mobile
  useEffect(() => {
    const checkScreenSize = () => setIsMobile(window.innerWidth < 768);
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Fetch datele organizate când componenta se montează
  useEffect(() => {
    if (fetchOrganizedData) {
      fetchOrganizedData();
    }
  }, [fetchOrganizedData]);

  // Sincronizare state local cu filters din hook (la încărcare)
  useEffect(() => {
    setLocalSearchValue(filters.search || '');
    setLocalClientName(filters.client_name || '');
    setLocalManagerId(filters.manager_id || '');
  }, [filters.search, filters.client_name, filters.manager_id]);

  // Utility functions
  const getStatusColor = (statusId) => {
    const status = statusOptions.find(option => option.id === statusId);
    return status ? status.color : '#8c8c8c';
  };

  const getStatusLabel = (statusId) => {
    const status = statusOptions.find(option => option.id === statusId);
    return status ? status.label : 'Necunoscut';
  };

  const getPurchaseStatus = (statusId) => {
    return productsInPurchaseStatuses?.find(status => status.id === statusId);
  };

  const calculateProfit = (salePrice, boughtPrice, quantity = 1) => {
    const sale = salePrice || 0;
    const bought = boughtPrice || 0;
    return (sale - bought) * quantity;
  };

  // Calculare sumar pentru o ofertă cu update local
  const calculateOfertaSummary = useCallback((oferta) => {
    const products = oferta.products || [];
    
    const purchaseRequestsCount = products.filter(p => p.purchase_info).length;

    const completedRequestsCount = products.filter(p => {
      return p.purchase_info && p.purchase_info.status_id >= 7;
    }).length;

    const summary = {
      totalProducts: products.length,
      totalValue: products.reduce((sum, p) => sum + ((p.sale_price || 0) * (p.product_qt || 1)), 0),
      totalBoughtValue: products.reduce((sum, p) => sum + ((p.bought_price || 0) * (p.product_qt || 1)), 0),
      totalProfit: products.reduce((sum, p) => sum + (((p.sale_price || 0) - (p.bought_price || 0)) * (p.product_qt || 1)), 0),
      purchaseRequestsCount,
      completedRequestsCount,
      purchaseProgress: products.length > 0 ? (purchaseRequestsCount / products.length) * 100 : 0,
      completionProgress: purchaseRequestsCount > 0 ? (completedRequestsCount / purchaseRequestsCount) * 100 : 0,
      lastAddedDate: oferta.oferta_info?.date_added,
      statusCount: products.reduce((acc, p) => {
        const statusId = p.product_status_id;
        const statusLabel = getStatusLabel(statusId);
        const statusColor = getStatusColor(statusId);
        acc[statusId] = {
          count: (acc[statusId]?.count || 0) + 1,
          color: statusColor,
          label: statusLabel
        };
        return acc;
      }, {})
    };


    console.log(summary)
    return summary;
  }, [statusOptions]);

  // Update local al sumarului unei oferte
  const updateOfertaSummary = useCallback((ofertaId) => {
    setOrganizedOferte(prevOferte => 
      prevOferte.map(oferta => {
        if (oferta.oferta_id === ofertaId) {
          const updatedSummary = calculateOfertaSummary(oferta);
          return {
            ...oferta,
            summary: updatedSummary
          };
        }
        return oferta;
      })
    );
  }, [calculateOfertaSummary, setOrganizedOferte]);

  // Funcție pentru aplicarea filtrelor (manual trigger)
  const handleApplyFilters = () => {
    const newFilters = {
      ...filters,
      search: localSearchValue,
      client_name: localClientName,
      manager_id: localManagerId ? parseInt(localManagerId) : null
    };
    applyFilters(newFilters);
  };

  // Handler pentru Enter key în input-uri
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  // Clear filters handler
  const handleClearFilters = () => {
    setLocalSearchValue('');
    setLocalClientName('');
    setLocalManagerId('');
    clearFilters();
  };

  // Modal handlers
  const openPriceModal = (product) => {
    setSelectedProduct(product);
    setNewBoughtPrice(product.bought_price || 0);
    setModalVisible(true);
  };

  const openFurnizoriDrawer = (product) => {
    setSelectedProduct(product);
    setDrawerVisible(true);
  };

  const handleSavePrice = async () => {
    if (!selectedProduct) return;
    
    try {
      const success = await changeBoughtPriceForProductOffer(
        selectedProduct.oferta_id,
        selectedProduct.id,
        newBoughtPrice
      );
      
      if (success) {
        updateOfertaSummary(selectedProduct.oferta_id);
        setModalVisible(false);
        setSelectedProduct(null);
        message.success('Prețul de achiziție a fost actualizat cu succes!');
      }
    } catch (error) {
      // message.error('Eroare la actualizarea prețului de achiziție');
    }
  };

  const handleCancel = () => {
    setModalVisible(false);
    setSelectedProduct(null);
    setNewBoughtPrice(0);
  };

  // Enhanced add product with local summary update
  const handleAddProduct = async (oferta_id, produs_to_oferta_id, furnizor_id, furnizor_name, bought_price) => {
    const success = await addProductToPurchaseRequest(
      oferta_id, 
      produs_to_oferta_id, 
      furnizor_id, 
      furnizor_name, 
      bought_price
    );
    
    if (success) {
      updateOfertaSummary(oferta_id);
      message.success('Produsul a fost adăugat la cererea de achiziție!');
    }
    
    return success;
  };

  // Enhanced delete product with local summary update
  const handleDeleteProduct = async (purchase_info_id, oferta_id) => {
    const success = await deleteProductFromPurchaseRequest(purchase_info_id);
    
    if (success) {
      updateOfertaSummary(oferta_id);
      message.success('Produsul a fost eliminat din cererea de achiziție!');
    }
    
    return success;
  };

  // Componentă pentru sumar ofertă responsive
  const OfertaSummary = ({ summary }) => {
    if (isMobile) {
      return (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <div style={{ textAlign: 'center', padding: '8px', background: '#f0f9ff', borderRadius: '6px' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>{summary.totalProducts}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Produse</div>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ textAlign: 'center', padding: '8px', background: '#f0f9ff', borderRadius: '6px' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: summary.totalProfit >= 0 ? '#52c41a' : '#ff4d4f' }}>
                  {summary.totalProfit.toFixed(0)}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Profit MDL</div>
              </div>
            </Col>
          </Row>
          
          <div style={{ background: '#fafafa', padding: '8px', borderRadius: '6px' }}>
            <div style={{ marginBottom: '4px', fontSize: '12px', fontWeight: 'bold' }}>
              Furnizori Deciși: {summary.purchaseRequestsCount}/{summary.totalProducts}
            </div>
            <Progress 
              percent={Math.round(summary.purchaseProgress)} 
              size="small" 
              status={summary.purchaseProgress === 100 ? 'success' : 'active'}
              showInfo={false}
            />
          </div>
          
          {summary.purchaseRequestsCount > 0 && (
            <div style={{ background: '#fafafa', padding: '8px', borderRadius: '6px' }}>
              <div style={{ marginBottom: '4px', fontSize: '12px', fontWeight: 'bold' }}>
                Gata de ridicare: {summary.completedRequestsCount}/{summary.purchaseRequestsCount}
              </div>
              <Progress 
                percent={Math.round(summary.completionProgress)} 
                size="small" 
                status={summary.completionProgress === 100 ? 'success' : 'active'}
                showInfo={false}
              />
            </div>
          )}
        </Space>
      );
    }

    return (
      <Space size="large" wrap>
        <Text strong>{summary.totalProducts} produse</Text>
        <Text strong>Vânzare: {summary.totalValue.toFixed(0)} MDL</Text>
        <Text strong>Cumpărare: {summary.totalBoughtValue.toFixed(0)} MDL</Text>
        <Text strong style={{ color: summary.totalProfit >= 0 ? '#52c41a' : '#ff4d4f' }}>
          Profit: {summary.totalProfit.toFixed(0)} MDL
        </Text>
        <Space direction="vertical" size="small">
          <div>
            <Text strong>Furnizori Deciși: </Text>
            <Progress 
              percent={Math.round(summary.purchaseProgress)} 
              size="small" 
              status={summary.purchaseProgress === 100 ? 'success' : 'active'}
              format={() => `${summary.purchaseRequestsCount}/${summary.totalProducts}`}
            />
          </div>
          {summary.purchaseRequestsCount > 0 && (
            <div>
              <Text strong>Gata de ridicare: </Text>
              <Progress 
                percent={Math.round(summary.completionProgress)} 
                size="small" 
                status={summary.completionProgress === 100 ? 'success' : 'active'}
                format={() => `${summary.completedRequestsCount}/${summary.purchaseRequestsCount}`}
              />
            </div>
          )}
        </Space>
        <Space size="small" wrap>
          {Object.entries(summary.statusCount).map(([statusId, status]) => (
            <Tag key={statusId} color={status.color}>
              {status.label} ({status.count})
            </Tag>
          ))}
        </Space>
      </Space>
    );
  };

  // Componentă pentru produse în format card (mobile)
  const ProductCard = ({ product, ofertaId }) => {
    const purchaseInfo = product.purchase_info;
    const profit = calculateProfit(product.sale_price, product.bought_price, product.product_qt);
    
    return (
      <AntCard 
        size="small" 
        style={{ marginBottom: 12 }}
        title={
          <div>
            <Text strong style={{ fontSize: 14 }}>
              {product.product?.denumire?.length > 60 
                ? `${product.product.denumire.substring(0, 60)}...` 
                : product.product?.denumire}
            </Text>
            <div style={{ marginTop: 4 }}>
              <Tag size="small" color="blue">SKU: {product.product?.sku_intern}</Tag>
              <Tag size="small" color={getStatusColor(product.product_status_id)}>
                {getStatusLabel(product.product_status_id)}
              </Tag>
            </div>
          </div>
        }
        extra={
          <Space>
            <Button 
              type="link" 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => openPriceModal(product)}
            />
            {product.furnizori?.length > 0 && (
              <Button 
                type="link" 
                size="small" 
                icon={<EyeOutlined />}
                onClick={() => openFurnizoriDrawer(product)}
              />
            )}
          </Space>
        }
      >
        <Row gutter={[8, 8]}>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 'bold' }}>{product.product_qt}</div>
              <div style={{ fontSize: 12, color: '#666' }}>Cantitate</div>
            </div>
          </Col>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 'bold', color: '#1890ff' }}>
                {product.bought_price ? `${product.bought_price}` : '-'}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>Cumpărare MDL</div>
            </div>
          </Col>
          <Col span={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 'bold', color: profit >= 0 ? '#52c41a' : '#ff4d4f' }}>
                {profit.toFixed(0)}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>Profit MDL</div>
            </div>
          </Col>
        </Row>

        {purchaseInfo ? (
          <div style={{ marginTop: 12, padding: 12, background: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f' }}>
            <Row align="middle" justify="space-between">
              <Col span={20}>
                <Space align="center">
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <Link to={`/furnizorpage/${purchaseInfo.furnizor_id}`}>
                    <Text strong style={{ color: '#52c41a' }}>{purchaseInfo.furnizor_name}</Text>
                  </Link>
                </Space>
                <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>
                  Request ID: {purchaseInfo.purchase_request_id}
                </div>
              </Col>
              <Col span={4}>
                <Button 
                  type="link" 
                  size="small" 
                  icon={<DeleteOutlined />} 
                  onClick={() => handleDeleteProduct(purchaseInfo.id, ofertaId)}
                />
              </Col>
            </Row>
          </div>
        ) : (
          product.furnizori?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Button 
                type="primary" 
                block 
                icon={<ShoppingCartOutlined />}
                onClick={() => openFurnizoriDrawer(product)}
              >
                Vezi Furnizori ({product.furnizori.length})
              </Button>
            </div>
          )
        )}
      </AntCard>
    );
  };

  // Coloane pentru tabel desktop
  const columns = [
    {
      title: 'Produs',
      dataIndex: ['product', 'denumire'],
      key: 'product_name',
      width: isMobile ? 200 : 400,
      render: (text, record) => {
        const truncateText = (text, maxLength = isMobile ? 50 : 150) => {
          if (!text) return '';
          if (text.length <= maxLength) return text;
          return text.substring(0, maxLength) + '...';
        };

        return (
          <div style={{ minWidth: isMobile ? 180 : 280 }}>
            <div style={{ marginBottom: 4 }}>
              <Text strong style={{ fontSize: isMobile ? 12 : 14, lineHeight: '20px', display: 'block' }}>
                {truncateText(text)}
              </Text>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
              <Tag size="small" color="blue">
                SKU: {record.product?.sku_intern}
              </Tag>
              {record.product?.unit_of_measure && (
                <Tag size="small" color="green">
                  {record.product.unit_of_measure}
                </Tag>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Cant.',
      dataIndex: 'product_qt',
      key: 'product_qt',
      width: isMobile ? 60 : 80,
      align: 'center',
    },
    {
      title: 'Preț Cump.',
      dataIndex: 'bought_price',
      key: 'bought_price',
      width: isMobile ? 100 : 120,
      render: (price, record) => (
        <Space direction={isMobile ? 'vertical' : 'horizontal'} size="small">
          <Text style={{ fontSize: isMobile ? 12 : 14 }}>
            {price ? `${price} MDL` : '-'}
          </Text>
          <Button 
            type="link" 
            size="small" 
            icon={<EditOutlined />}
            onClick={() => openPriceModal(record)}
            title="Editează prețul de achiziție"
          />
        </Space>
      ),
    },
    {
      title: 'Profit',
      key: 'profit',
      width: isMobile ? 80 : 100,
      align: 'center',
      render: (_, record) => {
        const profit = calculateProfit(record.sale_price, record.bought_price, record.product_qt);
        const profitColor = profit >= 0 ? '#52c41a' : '#ff4d4f';
        return (
          <Text strong style={{ color: profitColor, fontSize: isMobile ? 12 : 14 }}>
            {profit.toFixed(0)}
          </Text>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'product_status_id',
      key: 'product_status_id',
      width: isMobile ? 100 : 120,
      render: (status) => {
        const statusColor = getStatusColor(status);
        const statusLabel = getStatusLabel(status);
        return (
          <Tag color={statusColor} style={{ fontSize: isMobile ? 10 : 12 }}>
            {isMobile ? statusLabel.substring(0, 8) : statusLabel}
          </Tag>
        );
      },
    },
    ...(isMobile ? [] : [{
      title: 'Furnizori / Acțiuni',
      dataIndex: 'furnizori',
      key: 'furnizori',
      render: (furnizori, record) => {
        const purchaseInfo = record.purchase_info;
        
        if (purchaseInfo) {
          const status = getPurchaseStatus(purchaseInfo.status_id);
          return (
            <AntCard 
              size="small" 
              style={{ 
                background: 'linear-gradient(135deg, #f6ffed 0%, #e6f7d8 100%)', 
                border: '1px solid #b7eb8f',
                borderRadius: 8,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <Row align="middle" justify="space-between">
                <Col span={20}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
                    <Text strong style={{ color: '#52c41a', fontSize: 14 }}>
                      <Link to={`/furnizorpage/${purchaseInfo.furnizor_id}`}>
                        {purchaseInfo.furnizor_name}
                      </Link>
                    </Text>
                    {status && (
                      <Tag color={status.color} style={{ marginLeft: 'auto' }}>
                        <span style={{ marginLeft: 4 }}>{status.label}</span>
                      </Tag>
                    )}
                  </div>
                  
                  <div style={{ fontSize: 12, color: '#666', lineHeight: '18px' }}>
                    <div><strong>Request ID:</strong> {purchaseInfo.purchase_request_id}</div>
                    {purchaseInfo.termen_de_livrare && (
                      <div><strong>Termen livrare:</strong> {purchaseInfo.termen_de_livrare} zile</div>
                    )}
                  </div>
                </Col>
                <Col span={4}>
                  <Button 
                    type="link" 
                    size="small" 
                    icon={<DeleteOutlined />} 
                    onClick={() => handleDeleteProduct(purchaseInfo.id, record.oferta_id)}
                  />
                </Col>
              </Row>
            </AntCard>
          );
        }
        
        return (
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            {furnizori?.length > 0 ? (
              furnizori.map(furnizor => (
                <AntCard 
                  key={furnizor.id} 
                  size="small" 
                  style={{ 
                    background: 'linear-gradient(135deg, #f6faff 0%, #e6f3ff 100%)', 
                    border: '1px solid #91caff',
                    borderRadius: 8,
                  }}
                  hoverable
                >
                  <Row align="middle" justify="space-between">
                    <Col span={14}>
                      <div>
                        <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 2 }}>
                          {furnizor.furnizor_data?.denumire || 'Furnizor necunoscut'}
                        </Text>
                        <Text style={{ fontSize: 14, fontWeight: 'bold', color: '#1890ff' }}>
                          {furnizor.pret} {furnizor.valuta}
                        </Text>
                      </div>
                    </Col>
                    <Col span={10} style={{ textAlign: 'right' }}>
                      <Button 
                        type="primary" 
                        size="small" 
                        onClick={() => handleAddProduct(
                          record.oferta_id, 
                          record.id, 
                          furnizor.mongo_id_furnizor, 
                          furnizor.furnizor_data?.denumire || 'Furnizor necunoscut', 
                          furnizor.pret
                        )}
                      >
                        Selectează
                      </Button>
                    </Col>
                  </Row>
                </AntCard>
              ))
            ) : (
              <AntCard 
                size="small" 
                style={{ 
                  background: 'linear-gradient(135deg, #fff2f0 0%, #ffe7e1 100%)', 
                  border: '1px solid #ffccc7',
                  borderRadius: 8,
                  textAlign: 'center'
                }}
              >
                <ExclamationCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                <Text type="danger" strong style={{ fontSize: 12 }}>Nu sunt furnizori</Text>
              </AntCard>
            )}
          </Space>
        );
      },
    }])
  ];

  return (
    <>
      <FiltersComponent 
        localSearchValue={localSearchValue}
        setLocalSearchValue={setLocalSearchValue}
        localClientName={localClientName}
        setLocalClientName={setLocalClientName}
        localManagerId={localManagerId}
        setLocalManagerId={setLocalManagerId}
        filters={filters}
        applyFilters={applyFilters}
        statusOptions={statusOptions}
        handleKeyPress={handleKeyPress}
        handleApplyFilters={handleApplyFilters}
        handleClearFilters={handleClearFilters}
        loading={loading}
        isMobile={isMobile}
      />
      
      {/* Sumar general */}
      {overallSummary && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 8]} align="middle">
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}>
                  {overallSummary.totalOferte}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Oferte</div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 'bold', color: '#52c41a' }}>
                  {overallSummary.totalProducts}
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Produse</div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 'bold', color: '#fa8c16' }}>
                  {overallSummary.totalValue?.toFixed(0)} MDL
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Valoare</div>
              </div>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 'bold', color: overallSummary.totalProfit >= 0 ? '#52c41a' : '#ff4d4f' }}>
                  {overallSummary.totalProfit?.toFixed(0)} MDL
                </div>
                <div style={{ fontSize: 12, color: '#666' }}>Profit</div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ textAlign: 'center' }}>
                <Progress 
                  percent={Math.round(overallSummary.averagePurchaseProgress || 0)} 
                  size="small" 
                  status="active"
                  format={(percent) => `${percent}%`}
                />
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Progres mediu achiziții</div>
              </div>
            </Col>
          </Row>
        </Card>
      )}

      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Text>Se încarcă...</Text>
          </div>
        ) : (
          <>
            <Collapse 
              size={isMobile ? 'small' : 'default'}
              ghost={isMobile}
            >
              {organizedOferte.map((oferta) => {
                const summary = calculateOfertaSummary(oferta);
                return (
                  <Panel
                    header={
                      <div style={{ width: '100%' }}>
                        {isMobile ? (
                          <div>
                            <div style={{ marginBottom: 8 }}>
                              <Space wrap>
                                <ShopOutlined />
                                <Text strong style={{ fontSize: 14 }}>{oferta.client_name}</Text>
                                <Tag color="blue" size="small">ID: {oferta.oferta_id}</Tag>
                              </Space>
                            </div>
                            <OfertaSummary summary={summary} />
                          </div>
                        ) : (
                          <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                            <Col flex="auto">
                              <Space wrap>
                                <ShopOutlined />
                                <Text strong>{oferta.client_name}</Text>
                                <Tag color="blue">ID: {oferta.oferta_id}</Tag>
                                <Tag color="green">AMO ID: {oferta.oferta_info?.amo_id}</Tag>
                                {summary.purchaseProgress === 100 && (
                                  <Tag color="success" icon={<CheckCircleOutlined />}>
                                    Toate produsele comandate
                                  </Tag>
                                )}
                              </Space>
                            </Col>
                            <Col flex="none">
                              <OfertaSummary summary={summary} />
                            </Col>
                          </Row>
                        )}
                      </div>
                    }
                    key={oferta.oferta_id}
                  >
                    {isMobile ? (
                      <div>
                        {oferta.products.map(product => (
                          <ProductCard key={product.id} product={product} ofertaId={oferta.oferta_id} />
                        ))}
                      </div>
                    ) : (
                      <Table
                        columns={columns}
                        dataSource={oferta.products}
                        rowKey="id"
                        pagination={false}
                        scroll={{ x: true }}
                        size="middle"
                        rowClassName={(record) => {
                          const purchaseInfo = record.purchase_info;
                          if (purchaseInfo) {
                            return purchaseInfo.status_id >= 5 ? 'purchase-completed' : 'purchase-in-progress';
                          }
                          return '';
                        }}
                      />
                    )}
                  </Panel>
                );
              })}
            </Collapse>
            
            {/* Paginație pentru oferte */}
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Space>
                <Button 
                  disabled={pagination.current <= 1}
                  onClick={() => handleTableChange({ ...pagination, current: pagination.current - 1 })}
                >
                  Anterior
                </Button>
                <span>
                  Pagina {pagination.current} din {Math.ceil(pagination.total / pagination.pageSize)}
                </span>
                <Button 
                  disabled={pagination.current >= Math.ceil(pagination.total / pagination.pageSize)}
                  onClick={() => handleTableChange({ ...pagination, current: pagination.current + 1 })}
                >
                  Următor
                </Button>
              </Space>
            </div>
          </>
        )}
      </Card>

      {/* Drawer pentru furnizori (mobile) */}
      <Drawer
        title="Furnizori Disponibili"
        placement="bottom"
        height="70%"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        {selectedProduct && (
          <div>
            <div style={{ marginBottom: 16, padding: 12, background: '#f0f9ff', borderRadius: 8 }}>
              <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
                {selectedProduct.product?.denumire}
              </Title>
              <Space>
                <Tag color="blue">SKU: {selectedProduct.product?.sku_intern}</Tag>
                <Tag color="green">Cantitate: {selectedProduct.product_qt}</Tag>
              </Space>
            </div>

            <Divider>Furnizori</Divider>

            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {selectedProduct.furnizori?.length > 0 ? (
                selectedProduct.furnizori.map(furnizor => (
                  <AntCard 
                    key={furnizor.id} 
                    style={{ 
                      background: 'linear-gradient(135deg, #f6faff 0%, #e6f3ff 100%)', 
                      border: '1px solid #91caff',
                      borderRadius: 12,
                    }}
                  >
                    <Row align="middle" justify="space-between">
                      <Col span={16}>
                        <div>
                          <Text strong style={{ fontSize: 16, display: 'block', marginBottom: 4 }}>
                            {furnizor.furnizor_data?.denumire || 'Furnizor necunoscut'}
                          </Text>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                            <DollarOutlined style={{ color: '#1890ff' }} />
                            <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#1890ff' }}>
                              {furnizor.pret} {furnizor.valuta}
                            </Text>
                          </div>
                          {furnizor.furnizor_data?.website && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {furnizor.furnizor_data.website}
                            </Text>
                          )}
                        </div>
                      </Col>
                      <Col span={8} style={{ textAlign: 'right' }}>
                        <Button 
                          type="primary" 
                          size="large"
                          block
                          style={{ 
                            borderRadius: 8,
                            fontWeight: 'bold',
                            height: 44
                          }}
                          onClick={async () => {
                            const success = await handleAddProduct(
                              selectedProduct.oferta_id, 
                              selectedProduct.id, 
                              furnizor.mongo_id_furnizor, 
                              furnizor.furnizor_data?.denumire || 'Furnizor necunoscut',
                              furnizor.pret
                            );
                            if (success) {
                              setDrawerVisible(false);
                            }
                          }}
                        >
                          Selectează
                        </Button>
                      </Col>
                    </Row>
                  </AntCard>
                ))
              ) : (
                <AntCard 
                  style={{ 
                    background: 'linear-gradient(135deg, #fff2f0 0%, #ffe7e1 100%)', 
                    border: '1px solid #ffccc7',
                    borderRadius: 12,
                    textAlign: 'center',
                    padding: 24
                  }}
                >
                  <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: 24, marginBottom: 8 }} />
                  <Title level={5} type="danger">Nu sunt furnizori disponibili</Title>
                  <Text type="secondary">
                    Pentru acest produs nu există furnizori configurați în sistem.
                  </Text>
                </AntCard>
              )}
            </Space>
          </div>
        )}
      </Drawer>

      {/* Modal pentru editarea prețului de achiziție */}
      <Modal
        title="Editează Prețul de Achiziție"
        open={modalVisible}
        onOk={handleSavePrice}
        onCancel={handleCancel}
        okText="Salvează"
        cancelText="Anulează"
        width={isMobile ? '90%' : 400}
        centered={isMobile}
      >
        {selectedProduct && (
          <div>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text strong>Produs: </Text>
                <Text>{selectedProduct.product?.denumire}</Text>
              </div>
              <div>
                <Text strong>SKU: </Text>
                <Text>{selectedProduct.product?.sku_intern}</Text>
              </div>
              <div>
                <Text strong>Preț actual de achiziție: </Text>
                <Text>{selectedProduct.bought_price ? `${selectedProduct.bought_price} MDL` : 'Nu este setat'}</Text>
              </div>
              <div>
                <Text strong>Preț nou de achiziție:</Text>
                <InputNumber
                  value={newBoughtPrice}
                  onChange={setNewBoughtPrice}
                  min={0}
                  precision={2}
                  addonAfter="MDL"
                  style={{ width: '100%', marginTop: 8 }}
                  placeholder="Introduceți prețul nou"
                  size={isMobile ? 'large' : 'default'}
                />
              </div>
            </Space>
          </div>
        )}
      </Modal>

      {/* Stiluri CSS pentru rândurile din tabel */}
      <style jsx>{`
        .purchase-completed {
          background-color: #f6ffed !important;
        }
        .purchase-in-progress {
          background-color: #e6f7ff !important;
        }
        
        @media (max-width: 768px) {
          .ant-table {
            font-size: 12px;
          }
          .ant-table-thead > tr > th {
            padding: 8px 4px;
            font-size: 11px;
          }
          .ant-table-tbody > tr > td {
            padding: 8px 4px;
          }
          .ant-collapse-header {
            padding: 12px 16px !important;
          }
          .ant-card-head {
            padding: 0 12px;
            min-height: 40px;
          }
          .ant-card-body {
            padding: 12px;
          }
        }
      `}</style>
    </>
  );
};


export default DecideFurnizori; 